// FabricationOrderAcceptanceForm — the MOD acceptance form shown on the right when a fabrication
// order is selected, faithful to the official Annex G "Vehicle Inspection Protocol": an introductory
// purpose block, 4 sections (general info + product, required documents, functional tests,
// conclusions/decision/acceptance committee), a summary bar with the status badge + circuit actions,
// and Print. Content (docs/tests/fields/codes) comes from Annex G.
//
// Styled with MIC theme tokens only (Paper surfaces, theme.palette.action.hover for section
// shading, theme.palette.divider for borders, text.primary/text.secondary for typography) so the
// screen follows the app's light/dark theme instead of the hardcoded dark colors from the original
// prototype.
//
// #70 (GATE 1 r3) — the acceptance is a succession of versions per fabrication order, governed from a
// single toggle control:
//   - PENDING (a fresh version, never started) shows "Start acceptance" (POST /start) -> IN_PROGRESS,
//     and its fields are read-only until started (C3/S43).
//   - INTERRUPTED (a version started and then paused) shows "Resume acceptance" — the same control and
//     the same POST /start, only the label changes — and stays read-only until resumed (S31/S43).
//   - IN_PROGRESS shows "Close" and "Interrupt". "Close" derives the result from the form's final
//     decision (Passed/Failed) and sends it — with the whole on-screen form (C7) — in POST /finish; it
//     stays disabled until the decision is picked (C4/S32). "Interrupt" pauses the attempt
//     (POST /interrupt) without creating a version: the attempt moves to INTERRUPTED with its content
//     saved, and "Resume acceptance" resumes it editable (C2/S31).
//   - FINISHED FAILED re-offers "Start acceptance" (next version) when Sun marks it selectable;
//     FINISHED PASSED concludes the acceptance (no more versions) and stays read-only.
// `reopen` and the standalone "Discard" action were removed (RN6/CA4/C1): a closed version is
// immutable — correct it with a new version, and an open attempt is released by closing it.
//
// Send-by-email + document management (stories #2776/#73) are integrated on top of the versioning
// circuit: send the form / signed document by email, list every uploaded document, view/download/delete
// the active signed document and see the issued<->signed pairing and the send history. All of that is
// gated by the acceptance permission (RN11).
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControlLabel, IconButton, Paper, Radio, RadioGroup, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography
} from '@mui/material'
import {
  PrintRounded, PlayArrowRounded, UploadFileRounded, DoneAllRounded, PauseCircleOutlineRounded,
  HistoryRounded, EmailRounded, VisibilityRounded, DeleteOutlineRounded, AddRounded
} from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { useAuthorization } from '../generic/hooks/useAuthorization'
import useFrontEndConfig from '../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import { apiGet, apiPut, apiPostFormData, apiDownloadFile } from '../generic/Api_Functions'
import {
  getStatusLabelKey, getStatusChipProps, getResultLabelKey, getResultChipProps,
  FABRICATION_ORDER_ACCEPTANCE_STATUSES, FABRICATION_ORDER_ACCEPTANCE_RESULTS
} from './fabricationOrderAcceptanceStatus'
import FabricationOrderAcceptanceHistoryDialog from './FabricationOrderAcceptanceHistoryDialog'
import { sectionSx, ChecklistTable, CommitteeRow } from './fabricationOrderAcceptanceFormSections'
import {
  SECTION_1_FIELDS, REQUIRED_DOCUMENTS, FUNCTIONAL_TESTS, emptyChecklist,
  emptyCommittee, emptyCommitteePerson, normalizeCommittee
} from './fabricationOrderAcceptanceProtocol'
import { ACCEPTANCE_MAIL_DOCUMENT_TYPES } from './acceptanceMailTemplates'
import AcceptanceDocumentsList from './AcceptanceDocumentsList'
import FilePreviewDialog from './FilePreviewDialog'
import AcceptanceMailDialog from './AcceptanceMailDialog'
import AcceptanceMailHistory from './AcceptanceMailHistory'

// RN11 — sending by email and reading the send history require the same permission that already
// gates the acceptance form (no new granularity).
const ACCEPTANCE_MAIL_PERMISSION = 'FABRICATION_ORDER_ACCEPTANCE'

export default function FabricationOrderAcceptanceForm ({ fabricationOrder, onStart, onFinish, onInterrupt, onReload }) {
  const { t_ } = useSmartTranslation()
  const isUserAuthorized = useAuthorization()
  const [frontendConfig] = useFrontEndConfig()

  const [product, setProduct] = useState('')
  const [section1, setSection1] = useState({})
  const [documents, setDocuments] = useState(emptyChecklist(REQUIRED_DOCUMENTS))
  const [tests, setTests] = useState(emptyChecklist(FUNCTIONAL_TESTS))
  const [conclusions, setConclusions] = useState('')
  const [finalDecision, setFinalDecision] = useState('')
  const [committee, setCommittee] = useState(emptyCommittee())

  // #70 — read-only version history.
  const [historyOpen, setHistoryOpen] = useState(false)

  // HU-08/#70 item 8 — signed-PDF upload modal state. The signature is attached to the OPEN attempt
  // (IN_PROGRESS), before the close (8.2). Several signed documents and several evidences are allowed
  // per version (item 4).
  const [uploadOpen, setUploadOpen] = useState(false)
  const [signedFiles, setSignedFiles] = useState([])
  const [evidenceFiles, setEvidenceFiles] = useState([])
  const [previewFile, setPreviewFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [downloadingPdf, setDownloadingPdf] = useState(false)

  // Story #2776 — send-by-email dialog + send history. The token also refreshes the pairing view:
  // it must bump after every server-side document change (send, signed upload, signed delete),
  // otherwise the pair's presence flags go stale until the order is reselected.
  const [mailDialogOpen, setMailDialogOpen] = useState(false)
  const [mailDocumentType, setMailDocumentType] = useState(ACCEPTANCE_MAIL_DOCUMENT_TYPES.FORM)
  const [documentsRefreshToken, setDocumentsRefreshToken] = useState(0)

  const hydratedRef = useRef(false)
  const fabricationOrderId = fabricationOrder.fabricationOrderId

  const status = fabricationOrder.status
  const result = fabricationOrder.result
  const isPending = status === FABRICATION_ORDER_ACCEPTANCE_STATUSES.PENDING
  const isInProgress = status === FABRICATION_ORDER_ACCEPTANCE_STATUSES.IN_PROGRESS
  // #70 r3 — a version started and then paused with "Interrupt". It resumes (not starts) and, like
  // PENDING, is not editable.
  const isInterrupted = status === FABRICATION_ORDER_ACCEPTANCE_STATUSES.INTERRUPTED
  const isFinished = status === FABRICATION_ORDER_ACCEPTANCE_STATUSES.FINISHED
  const isPassed = result === FABRICATION_ORDER_ACCEPTANCE_RESULTS.PASSED
  // C3/S43 — the form is editable only while the attempt is in edition (IN_PROGRESS). A fresh PENDING
  // attempt, a paused INTERRUPTED attempt and a closed FINISHED version are all read-only.
  const readOnly = !isInProgress
  // The acceptance is concluded once a version closed PASSED — no further versions (RN-B/CA5).
  const isConcluded = isFinished && isPassed
  // The toggle control offers the start/resume action for a PENDING (fresh) or INTERRUPTED (paused)
  // attempt. For a terminal FINISHED version whether a retry can begin is a business decision that
  // belongs to Sun (RN7/RN-B): it reaches us through `selectable` — true for a FAILED that admits a new
  // version, false once concluded PASSED. The frontend trusts that flag instead of re-deriving the
  // decision from the result.
  const canStart = isPending || isInterrupted || (isFinished && Boolean(fabricationOrder.selectable))
  // #70 r3 — same button, same onStart; only the label changes: a paused version is "resumed", every
  // other startable case (fresh PENDING, retry after a FAILED close) is "started".
  const startLabelKey = isInterrupted ? 'Resume acceptance' : 'Start acceptance'
  // C4/S32 — the close result is derived from the form's final decision (Passed/Failed), no dialog.
  const closeResult = finalDecision === 'passed'
    ? FABRICATION_ORDER_ACCEPTANCE_RESULTS.PASSED
    : finalDecision === 'failed'
      ? FABRICATION_ORDER_ACCEPTANCE_RESULTS.FAILED
      : ''

  // RN11 (S22) — sending by email and the send-history section require the acceptance permission.
  const canManageAcceptanceMail = isUserAuthorized(ACCEPTANCE_MAIL_PERMISSION)
  // RN1/D8 — the final decision is marked (Passed/Failed).
  const finalDecisionMarked = finalDecision === 'passed' || finalDecision === 'failed'

  // #70 (item 8) — a version can only be CLOSED with the WHOLE protocol filled in: Section 1
  // (product + general info), every document and functional test evaluated (passed or failed),
  // conclusions, the acceptance committee (authority + contractor) and the final decision. Only
  // "Interrupt" (pause) is exempt — it deliberately saves a partial draft.
  const isFilled = (value) => typeof value === 'string' && value.trim().length > 0
  const isPersonComplete = (person) =>
    Boolean(person) && isFilled(person.rank) && isFilled(person.name) && isFilled(person.structure) && isFilled(person.date)
  const isRowEvaluated = (row) => Boolean(row.passed) || Boolean(row.failed)
  const isFormComplete =
    isFilled(product) &&
    SECTION_1_FIELDS.every((field) => isFilled(section1[field.key])) &&
    documents.every(isRowEvaluated) &&
    tests.every(isRowEvaluated) &&
    isFilled(conclusions) &&
    isPersonComplete(committee.authorityLeader) &&
    // #70 (point 4) — every added committee member must be complete too (rank/name/structure/DATE):
    // a member row left with an empty date (or any field) blocks the close.
    committee.members.every(isPersonComplete) &&
    isPersonComplete(committee.contractorRepresentative) &&
    finalDecisionMarked

  // RN6 — real backend flag (`GET .../list`), scoped to the current version (#70): at least one
  // non-deleted SIGNED document exists. `Boolean(...)` keeps missing data out of the business logic.
  const hasSignedDocument = Boolean(fabricationOrder.hasSignedDocument)
  // #70 item 8.2 — backend flag: the FORM email has been sent for the current version. This is what
  // unlocks "Upload signed PDF" in the reversed circuit.
  const formSent = Boolean(fabricationOrder.formSentForCurrentVersion)

  // #70 (item 8) — the reversed circuit, gated purely by the flags above:
  //   8.1 fill the form completely   -> "Send email" (sends the FORM) becomes available;
  //   8.2 send the form              -> "Upload signed PDF" becomes available (on the OPEN attempt);
  //   8.3 upload the signed document -> "Close acceptance" becomes available;
  //   8.4 close the version          -> upload disappears; only "Send email" (now the SIGNED set) stays.
  const canSendForm = canManageAcceptanceMail && ((isInProgress && isFormComplete) || isFinished)
  const canSendSigned = canManageAcceptanceMail && hasSignedDocument
  // Unified "Send email": emails the SIGNED set once any signed document exists, or the (unsigned)
  // acceptance FORM otherwise. The dialog states clearly which document(s) will be sent.
  const emailDocumentTypeToSend = hasSignedDocument
    ? ACCEPTANCE_MAIL_DOCUMENT_TYPES.SIGNED
    : ACCEPTANCE_MAIL_DOCUMENT_TYPES.FORM
  const canSendEmail = canSendSigned || canSendForm
  // 8.2/8.4 — the signed PDF is uploaded onto the OPEN attempt, only after the form has been sent;
  // once the version is closed the upload is no longer offered.
  const canUploadSigned = isInProgress && formSent

  // #70 item 6 — refresh key for the per-version sections (documents list, pairing, mail history).
  // They are all scoped to the CURRENT version in Sun, but the fabricationOrderId does not change
  // when the acceptance moves from version N to N+1 (same fabrication order), so keying their fetch
  // on the id alone leaves them showing the previous version's documents. Folding the version number
  // and status into the key makes them re-query on every version/state change too.
  const acceptanceDataKey = `${documentsRefreshToken}|${fabricationOrder.versionNumber ?? ''}|${status}`

  // D10/E6/S16 — normalize the API-boundary numbers (null when absent) to the dialog's own sentinel
  // (`false` = "unknown/not applicable"); an explicit check keeps a legitimate 0 from coercing to false.
  const signedDocumentSizeBytes = fabricationOrder.signedDocumentSizeBytes == null ? false : fabricationOrder.signedDocumentSizeBytes
  const maxMailAttachmentSizeBytes = (frontendConfig && frontendConfig.maxMailAttachmentSizeBytes) == null ? false : frontendConfig.maxMailAttachmentSizeBytes

  const vehicleName = section1.vehicleName || fabricationOrder.identification

  // 8.3 — Close is allowed only with a derived result, the whole protocol complete AND a signed
  // document already uploaded on the version (the signature is now prior to the close).
  const canClose = Boolean(closeResult) && isFormComplete && hasSignedDocument

  // HU-02/HU-06 — load the saved draft for the selected fabrication order; if none, reset + precarga
  // Section 1.
  useEffect(() => {
    hydratedRef.current = false
    const applyDefaults = () => {
      setProduct('')
      // #70 — vehicle name is NOT prefilled from the fabrication order; it starts blank.
      setSection1({ serial: fabricationOrder.identification || '' })
      setDocuments(emptyChecklist(REQUIRED_DOCUMENTS))
      setTests(emptyChecklist(FUNCTIONAL_TESTS))
      setConclusions('')
      setFinalDecision('')
      setCommittee(emptyCommittee())
    }
    apiGet(`fabricationOrderAcceptance/${fabricationOrderId}/form`)
      .then((saved) => {
        if (saved && saved.section1) {
          setProduct(saved.product || '')
          setSection1(saved.section1 || {})
          setDocuments(Array.isArray(saved.documents) && saved.documents.length ? saved.documents : emptyChecklist(REQUIRED_DOCUMENTS))
          setTests(Array.isArray(saved.tests) && saved.tests.length ? saved.tests : emptyChecklist(FUNCTIONAL_TESTS))
          setConclusions(saved.conclusions || '')
          setFinalDecision(saved.finalDecision || '')
          setCommittee(normalizeCommittee(saved.committee))
        } else {
          applyDefaults()
        }
      })
      .catch(applyDefaults)
      .finally(() => { hydratedRef.current = true })
  }, [fabricationOrderId, fabricationOrder.identification])

  // Passed and Failed are mutually exclusive per row: checking one clears the other (both may stay
  // unchecked = not evaluated yet).
  const updateRow = (setter) => (index, field, value) =>
    setter((prev) => prev.map((row, i) => {
      if (i !== index) return row
      const next = { ...row, [field]: value }
      if (field === 'passed' && value) next.failed = false
      if (field === 'failed' && value) next.passed = false
      return next
    }))

  const updateCommitteePerson = (role, field, value) =>
    setCommittee((prev) => ({ ...prev, [role]: { ...prev[role], [field]: value } }))

  const updateMember = (index, field, value) =>
    setCommittee((prev) => ({
      ...prev,
      members: prev.members.map((member, i) => (i === index ? { ...member, [field]: value } : member))
    }))

  const addMember = () =>
    setCommittee((prev) => ({ ...prev, members: [...prev.members, emptyCommitteePerson()] }))

  const removeMember = (index) =>
    setCommittee((prev) => ({ ...prev, members: prev.members.filter((_, i) => i !== index) }))

  // Aggregate the whole form for persistence (HU-06 / interrupt / close).
  const formData = useMemo(
    () => ({ product, section1, documents, tests, conclusions, finalDecision, committee }),
    [product, section1, documents, tests, conclusions, finalDecision, committee]
  )

  // HU-06 — autosave the draft (debounced) once the form has hydrated for this fabrication order,
  // while the attempt is in edition. A read-only (PENDING/paused or FINISHED) attempt is never re-saved.
  useEffect(() => {
    if (!hydratedRef.current || readOnly) return undefined
    const timer = setTimeout(() => {
      const request = apiPut(`fabricationOrderAcceptance/${fabricationOrderId}/form`, formData)
      if (request && typeof request.catch === 'function') {
        request.catch(() => {})
      }
    }, 800)
    return () => clearTimeout(timer)
  }, [formData, fabricationOrderId, readOnly])

  // C2/S31 — "Interrupt" pauses the attempt: it hands the current form content to the container, which
  // POSTs /interrupt (no version is created). The attempt goes to INTERRUPTED (read-only) with its
  // content saved; "Resume acceptance" resumes it editable.
  const interrupt = () => {
    if (onInterrupt) onInterrupt(fabricationOrderId, formData)
  }

  // C4/S32 — "Close" sends the result derived from the final decision to finish. Guarded by the button
  // being disabled while the final decision is empty. C7 (#70 r3) — it also sends the whole current
  // form (formData) so the frozen version reflects exactly what is on screen, not a stale debounced
  // autosave; this is what a new version inherits (RN9).
  const close = () => {
    if (!canClose || !onFinish) return
    onFinish(fabricationOrderId, closeResult, formData)
  }

  // HU-07 — download the server-generated acceptance PDF, with a loading state so the button gives
  // feedback while Sun renders the report (it can take a moment and otherwise looks like nothing happens).
  const downloadPdf = () => {
    setDownloadingPdf(true)
    apiDownloadFile(`fabricationOrderAcceptance/${fabricationOrderId}/pdf`, `fabrication-order-acceptance-${fabricationOrderId}.pdf`)
      .catch(() => {})
      .finally(() => setDownloadingPdf(false))
  }

  // HU-08/#70 item 8 — signed-PDF upload (attaches the signed document(s) to the OPEN attempt, 8.2).
  const openUpload = () => {
    setSignedFiles([])
    setEvidenceFiles([])
    setUploadError(null)
    setPreviewFile(null)
    setUploadOpen(true)
  }

  // Accumulative selection: every pick ADDS to the upload draft instead of replacing it (the
  // native file input resets its own selection on each dialog), so files can be gathered from
  // different folders in several rounds. Re-picking the same file (same name/size/lastModified)
  // is dropped instead of duplicated. Shared by signed documents and evidences (item 4: several of
  // each are allowed).
  const fileKey = (file) => `${file.name}|${file.size}|${file.lastModified}`
  const addFiles = (setter) => (picked) => {
    setter((prev) => {
      const alreadyPicked = new Set(prev.map(fileKey))
      return [...prev, ...picked.filter((file) => !alreadyPicked.has(fileKey(file)))]
    })
  }
  const addSignedFiles = addFiles(setSignedFiles)
  const addEvidenceFiles = addFiles(setEvidenceFiles)
  const removeSignedFile = (target) => setSignedFiles((prev) => prev.filter((file) => file !== target))
  const removeEvidenceFile = (target) => setEvidenceFiles((prev) => prev.filter((file) => file !== target))

  // item 4 — several signed documents (and evidences) per version. The backend endpoint pairs one
  // signed PDF (+ optional evidences) per call, so a multi-signed submit is uploaded sequentially:
  // the first call carries all the evidences, each extra signed document its own call. Any failure
  // stops and is surfaced (whatever was already uploaded stays — this version is still open).
  const submitUpload = async () => {
    if (signedFiles.length === 0 || uploading) return
    setUploading(true)
    setUploadError(null)
    try {
      for (let index = 0; index < signedFiles.length; index++) {
        const upload = new FormData()
        upload.append('signedDocument', signedFiles[index])
        if (index === 0) evidenceFiles.forEach((file) => upload.append('evidences', file))
        await apiPostFormData(`fabricationOrderAcceptance/${fabricationOrderId}/signed-document`, upload)
      }
      setUploadOpen(false)
      setDocumentsRefreshToken((token) => token + 1) // the pair's signed side just appeared
      if (onReload) onReload()
    } catch (error) {
      // Surface the failure instead of swallowing it (permission, illegal state, server error...).
      const message = (error && (error.message || error.body || error.statusText)) || ''
      setUploadError(`${t_('The signed PDF could not be uploaded.')}${message ? ` (${message})` : ''}`)
    } finally {
      setUploading(false)
    }
  }

  // Story #2776 — send-by-email dialog, shared by CU1 (form) and CU2 (signed document).
  const openMailDialog = (documentType) => {
    setMailDocumentType(documentType)
    setMailDialogOpen(true)
  }
  const closeMailDialog = () => setMailDialogOpen(false)
  // #70 item 8.2 — after a send, refresh the documents/pairing/history AND the acceptance DTO: sending
  // the FORM email flips `formSentForCurrentVersion`, which is what unlocks "Upload signed PDF".
  const onMailSent = () => {
    setDocumentsRefreshToken((token) => token + 1)
    if (onReload) onReload()
  }

  return (
    <Paper elevation={1} sx={{ p: 1.5 }}>
      {/* Summary bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
            {fabricationOrder.identification}
          </Typography>
          <Chip size='small' label={t_(getStatusLabelKey(status))} {...getStatusChipProps(status)} />
          {getResultLabelKey(result) && (
            <Chip size='small' data-testid='acceptance-result-chip' label={t_(getResultLabelKey(result))} {...getResultChipProps(result)} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* #70 — the current revision (attempt), shown big and clear in the header. */}
          {Boolean(fabricationOrder.versionNumber) && (
            <Typography sx={{ fontSize: 20, fontWeight: 800, mr: 1 }} data-testid='acceptance-version-header'>
              {`${t_('Version')} ${fabricationOrder.versionNumber}`}
            </Typography>
          )}
          <Button size='small' variant='text' startIcon={<HistoryRounded />} onClick={() => setHistoryOpen(true)}>
            {t_('Version history')}
          </Button>
          <Button
            size='small' variant='outlined' onClick={downloadPdf} disabled={downloadingPdf}
            startIcon={downloadingPdf ? <CircularProgress size={16} color='inherit' /> : <PrintRounded />}
          >
            {downloadingPdf ? t_('Generating PDF...') : t_('Print PDF')}
          </Button>

          {/* Toggle control (S30/S31): Start (or Resume, when paused) while a version can be
              (re)started, Close when one is open. */}
          {canStart && (
            <Button
              size='small' variant='contained' color='info' startIcon={<PlayArrowRounded />}
              onClick={() => onStart && onStart(fabricationOrderId)}
            >
              {t_(startLabelKey)}
            </Button>
          )}
          {isInProgress && (
            <>
              {/* C2/S31 — pause the attempt (no version created); "Resume acceptance" resumes it. */}
              <Button
                size='small' variant='outlined' color='inherit' startIcon={<PauseCircleOutlineRounded />}
                onClick={interrupt}
              >
                {t_('Interrupt')}
              </Button>
              {/* C4/S32 — close with the result derived from the final decision; disabled until the
                  whole protocol is complete (all criteria mandatory to close, Passed or Failed). */}
              <Button
                size='small' variant='contained' color='success' startIcon={<DoneAllRounded />}
                disabled={!canClose}
                onClick={close}
              >
                {t_('Close acceptance')}
              </Button>
            </>
          )}
          {/* 8.2/8.4 — the signed PDF is uploaded onto the OPEN attempt, after the form has been sent;
              once the version is closed it is no longer offered. */}
          {canUploadSigned && (
            <Button
              size='small' variant='outlined' color='success' startIcon={<UploadFileRounded />}
              onClick={openUpload}
            >
              {t_('Upload signed PDF')}
            </Button>
          )}
          {/* Send-by-email (stories #2776/#73), gated by the acceptance permission. One button:
              emails the signed document when one exists, or the unsigned form otherwise. View/download
              of the signed document live per-document in the documents list, not here. */}
          {canSendEmail && (
            <Button
              size='small' variant='outlined' startIcon={<EmailRounded />}
              onClick={() => openMailDialog(emailDocumentTypeToSend)}
            >
              {t_('Send email')}
            </Button>
          )}
        </Box>
      </Box>

      {isConcluded && (
        <Alert severity='success' sx={{ mb: 1.5 }} data-testid='acceptance-concluded-notice'>
          {t_('Acceptance concluded (PASSED). This version is read-only; no more versions can be started.')}
        </Alert>
      )}

      <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
        {t_('Vehicle Inspection Protocol — fill sections 1-4; sign on the printed PDF.')}
      </Typography>

      {/* Purpose / introduction (fixed, read-only text — Annex G foreword). */}
      <Box sx={sectionSx}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
          {t_('Purpose')}
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          {t_('The purpose of this protocol is to define the vehicle information, documentation and functional checks that will be jointly reviewed and carried out by the authority and the contractor for the vehicle official handover. It is divided in four sections and the final result will be categorized by the authority as passed or failed.')}
        </Typography>
        <Box component='ul' sx={{ pl: 3, m: 0, color: 'text.secondary', fontSize: 13 }}>
          <li>{t_('Section 1: General vehicle information')}</li>
          <li>{t_('Section 2: Required documents')}</li>
          <li>{t_('Section 3: Basic functional tests')}</li>
          <li>{t_('Section 4: Conclusions & Remarks')}</li>
        </Box>
      </Box>

      {/* Section 1 — general info + product */}
      <Box sx={sectionSx}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
          {t_('Section 1 · General vehicle information')}
        </Typography>
        <TextField
          label={t_('Product')}
          size='small'
          fullWidth
          disabled={readOnly}
          sx={{ mb: 1.5 }}
          value={product}
          onChange={(e) => setProduct(e.target.value)}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1.5 }}>
          {SECTION_1_FIELDS.map((field) => (
            <TextField
              key={field.key}
              label={`${field.code} ${t_(field.label)}`}
              placeholder={field.placeholder}
              size='small'
              disabled={readOnly}
              value={section1[field.key] || ''}
              onChange={(e) => setSection1((prev) => ({ ...prev, [field.key]: e.target.value }))}
            />
          ))}
        </Box>
      </Box>

      {/* Section 2 — required documents */}
      <ChecklistTable
        title={t_('Section 2 · Required documents')} items={REQUIRED_DOCUMENTS}
        rows={documents} onChange={updateRow(setDocuments)} disabled={readOnly} t_={t_}
      />

      {/* Section 3 — functional tests */}
      <ChecklistTable
        title={t_('Section 3 · Basic functional tests')} items={FUNCTIONAL_TESTS}
        rows={tests} onChange={updateRow(setTests)} disabled={readOnly} t_={t_}
      />

      {/* Section 4 — conclusions, final decision & acceptance committee */}
      <Box sx={sectionSx}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
          {t_('Section 4 · Conclusions, final decision & signatures')}
        </Typography>
        <TextField
          label={t_('Conclusions / remarks')} multiline minRows={3} fullWidth size='small'
          disabled={readOnly}
          sx={{ mb: 1.5 }} value={conclusions} onChange={(e) => setConclusions(e.target.value)}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
          <Typography variant='body2' color='text.secondary' sx={{ textTransform: 'uppercase' }}>
            {t_('Final decision')}
          </Typography>
          <RadioGroup row value={finalDecision} onChange={(e) => setFinalDecision(e.target.value)}>
            <FormControlLabel value='passed' disabled={readOnly} control={<Radio size='small' />} label={t_('Passed')} />
            <FormControlLabel value='failed' disabled={readOnly} control={<Radio size='small' />} label={t_('Failed')} />
          </RadioGroup>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>
          {t_('Committee')}
        </Typography>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell sx={{ color: 'text.secondary' }}>{t_('Rank')}</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>{t_('Name')}</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>{t_('Structure')}</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>{t_('Date')}</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>{t_('Signature')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <CommitteeRow
              label={t_('Authority Leader')}
              value={committee.authorityLeader}
              onChange={(field, value) => updateCommitteePerson('authorityLeader', field, value)}
              disabled={readOnly}
              signatureTestId='committee-signature-authority-leader'
              t_={t_}
            />
            {committee.members.map((member, index) => (
              <CommitteeRow
                key={index}
                label={`${t_('Member')} ${index + 1}`}
                value={member}
                onChange={(field, value) => updateMember(index, field, value)}
                onRemove={() => removeMember(index)}
                disabled={readOnly}
                signatureTestId={`committee-signature-member-${index}`}
                t_={t_}
              />
            ))}
            <CommitteeRow
              label={t_('Contractor representative')}
              value={committee.contractorRepresentative}
              onChange={(field, value) => updateCommitteePerson('contractorRepresentative', field, value)}
              disabled={readOnly}
              signatureTestId='committee-signature-contractor-representative'
              t_={t_}
            />
          </TableBody>
        </Table>
        {!readOnly && <Button size='small' onClick={addMember} sx={{ mt: 1 }}>{t_('Add member')}</Button>}
      </Box>

      {/* Story #73 follow-up / #70 item 6-7 — every uploaded file of the current version, with
          per-document view/download and (while the version is open) delete. */}
      {canManageAcceptanceMail && (
        <AcceptanceDocumentsList
          fabricationOrderId={fabricationOrderId}
          refreshToken={acceptanceDataKey}
          canDelete={isInProgress}
          onChanged={() => {
            setDocumentsRefreshToken((token) => token + 1)
            if (onReload) onReload()
          }}
        />
      )}

      {/* Story #2776 — send history (RN10/RN11, S19-S22): metadata-only, gated by the acceptance permission. */}
      {canManageAcceptanceMail && (
        <AcceptanceMailHistory fabricationOrderId={fabricationOrderId} refreshToken={acceptanceDataKey} />
      )}

      {/* HU-08/#70 item 4/8 — signed PDF(s) + evidences upload, attached to the OPEN attempt. Several
          signed documents and several evidences may be attached. */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>{t_('Upload signed PDF')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, mb: 1.5 }}>
            {t_('Attach the signed acceptance PDF(s) and, optionally, supporting evidences.')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Button size='small' variant='outlined' component='label' startIcon={<UploadFileRounded />}>
              {t_('Add signed PDFs')}
              <input
                hidden type='file' accept='.pdf' multiple aria-label={t_('Signed PDF')}
                onChange={(e) => {
                  addSignedFiles(Array.from(e.target.files || []))
                  e.target.value = ''
                }}
              />
            </Button>
            <Button size='small' variant='outlined' component='label' startIcon={<AddRounded />}>
              {t_('Add evidences')}
              <input
                hidden type='file' accept='.pdf,.jpg,.jpeg,.png' multiple aria-label={t_('Evidences')}
                onChange={(e) => {
                  addEvidenceFiles(Array.from(e.target.files || []))
                  e.target.value = ''
                }}
              />
            </Button>
          </Box>
          {(signedFiles.length > 0 || evidenceFiles.length > 0) && (
            <Box data-testid='upload-draft-files' sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {signedFiles.map((file) => (
                <Box key={fileKey(file)} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip size='small' color='success' label={t_('Signed document')} />
                  <Typography sx={{ flex: 1, fontSize: 13, overflowWrap: 'anywhere' }}>{file.name}</Typography>
                  <IconButton size='small' aria-label={t_('Preview')} onClick={() => setPreviewFile(file)}>
                    <VisibilityRounded fontSize='small' />
                  </IconButton>
                  <IconButton size='small' aria-label={t_('Remove')} onClick={() => removeSignedFile(file)}>
                    <DeleteOutlineRounded fontSize='small' />
                  </IconButton>
                </Box>
              ))}
              {evidenceFiles.map((file) => (
                <Box key={fileKey(file)} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip size='small' label={t_('Evidence')} />
                  <Typography sx={{ flex: 1, fontSize: 13, overflowWrap: 'anywhere' }}>{file.name}</Typography>
                  <IconButton size='small' aria-label={t_('Preview')} onClick={() => setPreviewFile(file)}>
                    <VisibilityRounded fontSize='small' />
                  </IconButton>
                  <IconButton size='small' aria-label={t_('Remove')} onClick={() => removeEvidenceFile(file)}>
                    <DeleteOutlineRounded fontSize='small' />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          {uploadError && (
            <Alert severity='error' sx={{ mt: 1.5 }} onClose={() => setUploadError(null)}>
              {uploadError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>{t_('Cancel')}</Button>
          <Button
            variant='contained' color='success' disabled={signedFiles.length === 0 || uploading} onClick={submitUpload}
            startIcon={uploading ? <CircularProgress size={16} color='inherit' /> : <UploadFileRounded />}
          >
            {uploading ? t_('Uploading...') : t_('Upload signed PDF')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Local preview of a file from the upload draft (before anything is uploaded). */}
      <FilePreviewDialog
        open={Boolean(previewFile)}
        onClose={() => setPreviewFile(null)}
        file={previewFile}
      />

      {/* Story #2776 — send-by-email dialog, shared by CU1 (form) and CU2 (signed document). */}
      <AcceptanceMailDialog
        open={mailDialogOpen}
        documentType={mailDocumentType}
        fabricationOrderId={fabricationOrderId}
        fabricationOrderCode={fabricationOrder.identification}
        vehicleName={vehicleName}
        versionNumber={fabricationOrder.versionNumber}
        attachmentSizeBytes={mailDocumentType === ACCEPTANCE_MAIL_DOCUMENT_TYPES.SIGNED ? signedDocumentSizeBytes : false}
        maxAttachmentSizeBytes={maxMailAttachmentSizeBytes}
        onClose={closeMailDialog}
        onSent={onMailSent}
      />

      {/* #70 — read-only version history + drill-down to each version's saved form (C8/S50). */}
      <FabricationOrderAcceptanceHistoryDialog
        fabricationOrderId={fabricationOrderId}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </Paper>
  )
}
